/**
 * BrandedReportHeader — single source of truth for the report header shown on:
 *   - Wizard Step 8 (Review) preview
 *   - SummaryReportViewPage (admin read-only)
 *   - ClientReportViewPage (client portal read-only)
 *   - SummaryStockAuditReportPage (admin edit/detail view)
 *
 * Two header implementations previously existed and drifted out of sync.
 * This is the canonical component — do not re-inline elsewhere.
 */
import React from 'react';
import { Button } from './ui/button';
import { StoredLogo } from './shared/StoredLogo';
import { FileText, Wine, UtensilsCrossed } from 'lucide-react';

const ABBEY_COLORS = {
  primary: '#1e3a5f',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

export const getScopeInfo = (scope) => {
  if (scope === 'FOOD') return { label: 'Food', color: 'text-orange-600', icon: <UtensilsCrossed className="h-5 w-5" /> };
  if (scope === 'SHOP') return { label: 'Shop', color: 'text-purple-600', icon: <UtensilsCrossed className="h-5 w-5" /> };
  return { label: 'Beverage', color: 'text-blue-600', icon: <Wine className="h-5 w-5" /> };
};

const BrandedReportHeader = ({
  report,
  scopeInfo: scopeInfoProp,
  showExportPdf = false,
  onExportPdf = null,
  isExporting = false,
}) => {
  const scopeInfo = scopeInfoProp || getScopeInfo(report?.scope);
  const clientLogoSrc = report?.client_logo_url || null;

  return (
    <div className="border-b-2 pb-4 mb-6 print:pb-6 print:mb-8" style={{ borderColor: ABBEY_COLORS.primary }}>
      <div className="flex items-center gap-6">
        {clientLogoSrc && (
          <div className="flex-shrink-0">
            <StoredLogo
              logoKey={clientLogoSrc}
              alt={report.client_name}
              className="w-auto object-contain"
              data-testid="report-client-logo"
              style={{ height: '96px', maxWidth: '160px' }}
            />
          </div>
        )}
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-xl font-bold print:text-2xl" style={{ color: ABBEY_COLORS.primary }}>
              {scopeInfo.label} Stock Audit Report
            </h1>
            {showExportPdf && onExportPdf && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExportPdf}
                disabled={isExporting}
                className="print:hidden"
                data-testid="export-pdf-btn"
              >
                {isExporting ? (
                  <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Export PDF
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm print:text-base max-w-md mx-auto">
            {report?.parent_client_id && (
              <div className="text-center col-span-2">
                <span className="text-slate-500">Client:</span>{' '}
                <span className="font-medium">{report.client_name}</span>
              </div>
            )}
            <div className="text-center col-span-2">
              <span className="text-slate-500">Site:</span>{' '}
              <span className="font-medium">{report.site_name}</span>
            </div>
            <div className="text-right">
              <span className="text-slate-500">Audit Date:</span>{' '}
              <span className="font-medium">{formatDate(report.audit_date)}</span>
            </div>
            <div className="text-left">
              <span className="text-slate-500">Scope:</span>{' '}
              <span className={`font-medium ${scopeInfo.color}`}>{scopeInfo.label}</span>
            </div>
            {report.audit_date && report.stock_period_days > 0 && (() => {
              // 2026-04-24 iter322 — Trust the hardened backend triple.
              // `report.previous_audit_date` is server-derived and already
              // satisfies `previous_audit_date + stock_period_days = audit_date`.
              // Fall back to UTC-anchored ms math only when the backend
              // hasn't supplied it (older PDFs regenerated without iter322).
              let startIso = report.previous_audit_date;
              if (!startIso) {
                const MS_PER_DAY = 86400000;
                const endMs = new Date(report.audit_date + 'T00:00:00Z').getTime();
                startIso = new Date(endMs - report.stock_period_days * MS_PER_DAY)
                  .toISOString().split('T')[0];
              }
              return (
                <div className="text-center col-span-2 mt-1 pt-1 border-t border-slate-200">
                  <span className="text-slate-500">Stock Period:</span>{' '}
                  <span className="font-medium">
                    {formatDate(startIso)} — {formatDate(report.audit_date)}
                  </span>
                  <span className="text-slate-400 ml-1">({report.stock_period_days} days)</span>
                </div>
              );
            })()}
          </div>
        </div>
        <div className="flex-shrink-0">
          <img
            src="/logo.png"
            alt="Abbey 5 Star Ltd"
            className="w-auto object-contain print:hidden"
            style={{ height: '77px' }}
          />
          <img
            src="/logo.png"
            alt="Abbey 5 Star Ltd"
            className="hidden print:block w-auto object-contain"
            style={{ height: '80px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default BrandedReportHeader;
